import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ICreateNoteUsingCalendarEventOperation } from "@/store/sync/operations/types";
import {
  OptimisticSyncUpdate,
  SyncCustomErrorData,
  SyncModelData,
  SyncOperationKind,
} from "@/store/sync/types";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";
import { resolveNoteSourceSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteSourceSyncModelUuid";
import { SourceModelData, SourceUpsertedSyncUpdateValue } from "@/store/sources/types";
import { NoteSourceModelData, NoteSourceUpsertedSyncUpdateValue } from "@/store/sources/types";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { Uuid } from "@/domains/global/identifiers";
import { BaseSyncOperationParams } from "@/store/sync/operations/BaseSyncOperation";
import { UNTITLED_CALENDAR_EVENT_TITLE, UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { CreateNoteUsingTemplateOperation } from "@/store/sync/operations/templates/CreateNoteUsingTemplateOperation";
import { notesModule } from "@/modules/notes";
import {
  NoteContentDocumentModelData,
  NoteContentDocumentUpsertedSyncUpdateValue,
  NoteModelData,
  NoteUpsertedSyncUpdateValue,
} from "@/store/note";

export class CreateNoteUsingCalendarEventOperation extends BaseSyncOperation<ICreateNoteUsingCalendarEventOperation> {
  private calendarEvent?: SpaceAccountCalendarEventObservable;
  private readonly spaceAccountCalendarEventId: Uuid;

  constructor(
    params: BaseSyncOperationParams<ICreateNoteUsingCalendarEventOperation> & {
      space_account_calendar_event_id: Uuid;
    }
  ) {
    super(params);
    this.spaceAccountCalendarEventId = params.space_account_calendar_event_id;
  }

  get operationKind(): SyncOperationKind {
    return "CREATE_NOTE_USING_CALENDAR_EVENT";
  }

  async execute() {
    this.calendarEvent = await this.store.spaceAccountCalendarEvents.getAsync(
      this.spaceAccountCalendarEventId
    );

    await super.execute();
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const syncUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];

    const { primaryLabel, secondaryLabel } = notesModule.convertEncodedContentToNoteContent(
      this.payload.encoded_content
    );
    if (this.payload.template_id) {
      // If template_id is provided, use create note with template
      const createNoteWithTemplateOperation = await new CreateNoteUsingTemplateOperation({
        store: this.store,
        payload: {
          note_id: this.payload.note_id,
          template_id: this.payload.template_id,
          encoded_content: this.payload.encoded_content,
        },
      }).generateOptimisticUpdates();
      syncUpdates.push(...createNoteWithTemplateOperation);
    } else {
      // If template_id is not provided, create note normally
      // 1. Create the note
      const noteValue: NoteUpsertedSyncUpdateValue = {
        model_id: this.payload.note_id,
        model_kind: "NOTE",
        model_version: 0,
        model_data: {
          trashed_at: null,
          created_at: this.committedAt,
          locally_created_at: this.committedAt,
          locally_modified_at: this.committedAt,
          owned_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          created_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          modified_by_space_account_ids: [this.store.spaceAccounts.myPersonalSpaceAccountId],
          primary_label: primaryLabel || UNTITLED_NOTE_TITLE,
          primary_label_strategy: "INFERRED_FROM_CONTENT",
          secondary_label: secondaryLabel || "",
          secondary_label_strategy: "INFERRED_FROM_CONTENT",
          media_kinds: [],
          mentioned_note_ids: [],
          is_empty: false,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      };
      const noteUpserted: OptimisticSyncUpdate<NoteModelData> = {
        optimistic_update_id: uuidModule.generate(),
        locally_committed_at: this.committedAt,
        kind: "UPSERTED",
        value: noteValue,
      };
      syncUpdates.push(noteUpserted);

      // 2. Create the note content document
      const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
        noteId: this.payload.note_id,
      });
      const noteContentDocumentValue: NoteContentDocumentUpsertedSyncUpdateValue = {
        model_id: noteContentDocumentId,
        model_kind: "NOTE_CONTENT_DOCUMENT",
        model_version: 0,
        model_data: {
          note_id: this.payload.note_id,
          encoded_content: this.payload.encoded_content,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      };
      const noteContentDocumentUpserted: OptimisticSyncUpdate<NoteContentDocumentModelData> = {
        optimistic_update_id: uuidModule.generate(),
        locally_committed_at: this.committedAt,
        kind: "UPSERTED",
        value: noteContentDocumentValue,
      };
      syncUpdates.push(noteContentDocumentUpserted);
    }

    // 3. Create the source sync model
    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.payload.connected_calendar_event_id,
    });
    const sourceValue: SourceUpsertedSyncUpdateValue = {
      model_id: sourceId,
      model_kind: "SOURCE",
      model_version: 0,
      model_data: {
        id: sourceId,
        source_kind: "CALENDAR_EVENT",
        associated_artifact_id: this.payload.connected_calendar_event_id,
        primary_label: this.calendarEvent?.primaryLabel || UNTITLED_CALENDAR_EVENT_TITLE,
        secondary_label: null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const sourceUpserted: OptimisticSyncUpdate<SourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: sourceValue,
    };
    syncUpdates.push(sourceUpserted);

    // 4. Create the note_source sync model
    const noteSourceId = resolveNoteSourceSyncModelUuid({
      noteId: this.payload.note_id,
      sourceId,
    });
    const noteSourceValue: NoteSourceUpsertedSyncUpdateValue = {
      model_id: noteSourceId,
      model_kind: "NOTE_SOURCE",
      model_version: 0,
      model_data: {
        id: noteSourceId,
        note_id: this.payload.note_id,
        source_id: sourceId,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const noteSourceUpserted: OptimisticSyncUpdate<NoteSourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteSourceValue,
    };
    syncUpdates.push(noteSourceUpserted);

    return syncUpdates;
  }

  async triggerRecompute() {
    if (this.payload.template_id) {
      await new CreateNoteUsingTemplateOperation({
        store: this.store,
        payload: {
          note_id: this.payload.note_id,
          template_id: this.payload.template_id,
          encoded_content: this.payload.encoded_content,
        },
      }).triggerRecompute();
    } else {
      await this.store.notes.recompute(this.payload.note_id);
      const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
        noteId: this.payload.note_id,
      });
      await this.store.noteContentDocuments.recompute(noteContentDocumentId);
    }

    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.payload.connected_calendar_event_id,
    });
    await this.store.sources.recompute(sourceId);

    const noteSourceId = resolveNoteSourceSyncModelUuid({
      noteId: this.payload.note_id,
      sourceId,
    });
    await this.store.noteSources.recompute(noteSourceId);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
